/**
 * What a meditation's listening is worth to GA4 (plan 2026_09_29_almanach_quies.md, A2-A4): one
 * `audio_play` per item on its first real start, one `audio_completed` per item, and
 * `audio_listened` at each threshold of time actually played, `audio_progress` at each share of the
 * meditation's length actually played. The element's own events are not the
 * unit: a pause, a seek or a stream->file swap resumes the same element, and a replay after the end
 * plays it again. The item is the logical meditation, named by the host, and its record outlives the
 * page load (localStorage), so a reload or a return after signup resumes the meditation, not restarts
 * its counts.
 */
import type {AudioContext, AudioParams, AudioSourceKind, AudioStopReason, QuiesEventMap} from '@product/quies/utils/analytics'

export const LISTENED_MINUTES = [1, 3, 5, 10, 15, 20] as const
export const PROGRESS_PERCENTS = [25, 50, 75, 100] as const

/** Seconds short of the length that still count as the whole: the last `timeupdate` lands up to
 *  250 ms before the end, and each resume re-anchors without counting the instant before it. */
const END_TOLERANCE_SECONDS = 2

/** Seconds of playing time accepted from one `timeupdate` to the next. A position that moved further
 *  than the wall clock allows was not played: a seek, or a reload restoring its position. */
const CLOCK_TOLERANCE_SECONDS = 1

export function playedDelta(from: {position: number; wallMs: number} | null, position: number, wallMs: number): number {
    if (!from) return 0
    const moved = position - from.position
    const elapsed = (wallMs - from.wallMs) / 1000
    if (moved <= 0 || moved > elapsed + CLOCK_TOLERANCE_SECONDS) return 0
    return moved
}

export function audioParams(context: AudioContext): AudioParams {
    const {item: _item, ...rest} = context
    const {sessionId: _sessionId, ...params} = rest as typeof rest & {sessionId?: unknown}
    return params
}

/** Aggregate of what a meditation's listening came to, for the server's ledger. */
export interface ListenReport {
    playedSeconds: number
    position: number
    /** The last `audio_stopped` reason, or `failed`; absent when the report is not about a stop. */
    stop?: string
}

/** Lifecycle reports (page hidden or leaving) come at most this often. */
const LIFECYCLE_REPORT_INTERVAL_MS = 15_000

/** Seconds of played time between two saves of the record while nothing else is worth one. */
const SAVE_EVERY_PLAYED_SECONDS = 5

const STORE_KEY = 'quies:listening'
const STORE_KEPT = 50

const hiddenNow = () => typeof document !== 'undefined' && document.hidden

interface ItemRecord {
    started: boolean
    completed: boolean
    playedSeconds: number
    minutesReached: number
    percentReached: number
}

type StoredItem = ItemRecord & {item: string}

function isStored(value: unknown): value is StoredItem {
    const v = value as Record<string, unknown> | null
    return !!v && typeof v.item === 'string' && typeof v.started === 'boolean' && typeof v.completed === 'boolean'
        && typeof v.playedSeconds === 'number' && typeof v.minutesReached === 'number' && typeof v.percentReached === 'number'
}

function readStore(): StoredItem[] {
    try {
        const stored: unknown = JSON.parse(localStorage.getItem(STORE_KEY) ?? '[]')
        return Array.isArray(stored) ? stored.filter(isStored) : []
    } catch {
        return []
    }
}

function writeStore(item: string, record: ItemRecord): void {
    const others = readStore().filter((entry) => entry.item !== item)
    try {
        localStorage.setItem(STORE_KEY, JSON.stringify([...others, {item, ...record}].slice(-STORE_KEPT)))
    } catch {
        // No storage: the record still holds within this page load.
    }
}

/** In page: an item remounted (a new player for the same meditation) keeps its record. */
const items = new Map<string, ItemRecord>()

function recordOf(item: string): ItemRecord {
    let record = items.get(item)
    if (!record) {
        const {item: _item, ...stored} = readStore().find((entry) => entry.item === item)
            ?? {item, started: false, completed: false, playedSeconds: 0, minutesReached: 0, percentReached: 0}
        record = stored
        items.set(item, record)
    }
    return record
}

export type ListeningEvent =
    | 'audio_play' | 'audio_completed' | 'audio_listened' | 'audio_progress'
    | 'audio_paused' | 'audio_stopped' | 'audio_failed'

export type ListeningEmit = <E extends ListeningEvent>(event: E, params: QuiesEventMap[E]) => void

/** What the player knows when its element reaches `ended`. */
export interface EndedFacts {
    /** The element plays the live stream, not the stored file. */
    onStream: boolean
    /** The browser has seen the voicing complete: the stream it plays is the whole meditation. */
    voicingComplete: boolean
    position: number
    lengthSeconds: number
}

/** The element's state at the moment of a stop or a failure. */
export interface StopFacts {
    position: number
    bufferedSeconds: number
    source: AudioSourceKind
}

/** The share of `lengthSeconds` that `playedSeconds` covers, in percent; the last step is reached
 *  within END_TOLERANCE_SECONDS of the length. 0 while the length is unknown. */
export function playedPercent(playedSeconds: number, lengthSeconds: number): number {
    if (!(lengthSeconds > END_TOLERANCE_SECONDS)) return 0
    if (playedSeconds >= lengthSeconds - END_TOLERANCE_SECONDS) return 100
    return (playedSeconds / lengthSeconds) * 100
}

/** Driven by the player's element events; emits through `emit`, and tells `report` what the
 *  listening has come to. `interrupt` covers every moment the position may jump or stop: pause,
 *  seeking, a reload (emptied), a stall. */
export function createListeningTracker(context: AudioContext, emit: ListeningEmit, report?: (report: ListenReport) => void) {
    const record = recordOf(context.item)
    const params = audioParams(context)
    let anchor: {position: number; wallMs: number} | null = null
    let position = 0
    let wallMs = 0
    let lastSent: {wallMs: number; playedSeconds: number} | null = null
    let savedPlayedSeconds = record.playedSeconds

    const save = () => {
        writeStore(context.item, record)
        savedPlayedSeconds = record.playedSeconds
    }
    const send = (stop?: string) => {
        lastSent = {wallMs, playedSeconds: record.playedSeconds}
        report?.({playedSeconds: Math.floor(record.playedSeconds), position: Math.round(position), ...(stop ? {stop} : {})})
    }

    const interrupt = () => {
        anchor = null
        if (record.playedSeconds !== savedPlayedSeconds) save()
    }

    return {
        playing(at: number, wall: number) {
            anchor = {position: at, wallMs: wall}
            position = at
            wallMs = wall
            if (record.started) return
            record.started = true
            save()
            emit('audio_play', params)
        },
        /** `lengthSeconds`: the meditation's length, 0 while unknown (the live stream before its file
         *  is known); the played time keeps accruing meanwhile, so the shares catch up once it is. */
        time(at: number, wall: number, lengthSeconds = 0) {
            record.playedSeconds += playedDelta(anchor, at, wall)
            anchor = {position: at, wallMs: wall}
            position = at
            wallMs = wall
            let crossed = false
            for (const minutes of LISTENED_MINUTES) {
                if (minutes <= record.minutesReached || record.playedSeconds < minutes * 60) continue
                record.minutesReached = minutes
                crossed = true
                emit('audio_listened', {...params, minutes, hidden: hiddenNow()})
            }
            const share = playedPercent(record.playedSeconds, lengthSeconds)
            for (const percent of PROGRESS_PERCENTS) {
                if (percent <= record.percentReached || share < percent) continue
                record.percentReached = percent
                crossed = true
                emit('audio_progress', {...params, percent, hidden: hiddenNow()})
            }
            if (crossed) {
                save()
                send()
            } else if (record.playedSeconds - savedPlayedSeconds >= SAVE_EVERY_PLAYED_SECONDS) save()
        },
        interrupt,
        /** The element paused. Only the person's pause is an `audio_paused`: not the one before `ended`,
         *  not the reload a stream->file swap or a retry causes, not the page's own unload. */
        pause(at: number, cause: {ended: boolean; reloading: boolean; unloading: boolean}) {
            interrupt()
            if (cause.ended || cause.reloading || cause.unloading) return
            position = at
            emit('audio_paused', {...params, at_seconds: Math.round(at), hidden: hiddenNow()})
        },
        /** The element reached `ended`. A meditation is completed when it was heard whole: the file,
         *  a stream the voicing had finished feeding, or a position at the length. A live stream that
         *  closed under an unfinished voicing is a stop, for the caller to report through `stopped`. */
        ended(facts: EndedFacts): 'completed' | 'stopped' {
            anchor = null
            position = facts.position
            const whole = !facts.onStream || facts.voicingComplete
                || (facts.lengthSeconds > 0 && facts.position >= facts.lengthSeconds - END_TOLERANCE_SECONDS)
            if (!whole) return 'stopped'
            if (!record.completed) {
                record.completed = true
                emit('audio_completed', {...params, hidden: hiddenNow()})
                save()
                send()
            }
            return 'completed'
        },
        stopped(reason: AudioStopReason, facts: StopFacts) {
            position = facts.position
            emit('audio_stopped', {
                ...params,
                at_seconds: Math.round(facts.position),
                buffered_seconds: Math.round(facts.bufferedSeconds),
                source: facts.source,
                reason,
                hidden: hiddenNow(),
            })
            save()
            send(reason)
        },
        failed(facts: {position: number; source: AudioSourceKind; code: number; networkState: number}) {
            position = facts.position
            emit('audio_failed', {
                ...params,
                at_seconds: Math.round(facts.position),
                source: facts.source,
                code: facts.code,
                network_state: facts.networkState,
            })
            save()
            send('failed')
        },
        /** The page is hidden or leaving: keep the record, and report when something new was played
         *  and the last report is old enough. */
        flush(at: number, wall: number) {
            position = at
            wallMs = wall
            save()
            if (record.playedSeconds <= (lastSent?.playedSeconds ?? 0)) return
            if (lastSent && wall - lastSent.wallMs < LIFECYCLE_REPORT_INTERVAL_MS) return
            send()
        },
    }
}

/** Tests only: a fresh page load. */
export function forgetListening(): void {
    items.clear()
}
